import axios from 'axios';
import CryptoJS from 'crypto-js';

/** AES-128-CBC (16-char key/iv) — must match public/api/gateway.php */
const AES_KEY = CryptoJS.enc.Utf8.parse('LxQY43Nc&87x8nxw');
const AES_IV = CryptoJS.enc.Utf8.parse('@@@@&&&&####$$$$');

export const API_ORIGIN = 'https://dhanwalle.in';

/** Action codes — Network tab shows only these via /api/gateway.php */
export const GW = {
  GET_STATE: 'a1',
  GET_CITY: 'a2',
  GET_PINCODE: 'a3',
  SEND_OTP: 'm1',
  VERIFY_OTP: 'm2',
  VERIFY_PAN: 'p1',
  PAN_BLACKLIST: 'p2',
  PAN_CUSTOMER: 'p3',
  SEND_AADHAAR_OTP: 'k1',
  VERIFY_AADHAAR_OTP: 'k2',
  SAVE_ENQUIRY: 'e1',
  SAVE_LEAD: 'l1',
};

export function shouldUseApiGateway() {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  const isLocal = host === 'localhost' || host === '127.0.0.1';
  if (process.env.REACT_APP_DISABLE_API_GATEWAY === '1') return false;
  if (process.env.REACT_APP_FORCE_API_GATEWAY === '1') return true;
  return !isLocal;
}

export function gatewayUrl() {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${base}/api/gateway.php`;
}

/**
 * Encrypt JSON payload → Base64 ciphertext ONLY
 * (not OpenSSL "Salted__" full string)
 */
export function encryptGatewayPayload(payload) {
  const plain = JSON.stringify(payload ?? {});
  const encrypted = CryptoJS.AES.encrypt(plain, AES_KEY, {
    iv: AES_IV,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });
  return encrypted.ciphertext.toString(CryptoJS.enc.Base64);
}

export function buildGatewayEnvelope(action, payload = {}) {
  return {
    a: action,
    d: encryptGatewayPayload(payload),
  };
}

export async function legacyRequest({
  method = 'GET',
  url,
  data,
  params,
  headers = {},
  formUrlEncoded = false,
  withCredentials = false,
} = {}) {
  const config = {
    method,
    url,
    params,
    headers: { ...headers },
    validateStatus: () => true,
    withCredentials,
  };

  if (formUrlEncoded) {
    const body =
      data instanceof URLSearchParams
        ? data
        : new URLSearchParams(data || {});
    config.data = body;
    config.headers['Content-Type'] =
      config.headers['Content-Type'] ||
      'application/x-www-form-urlencoded';
  } else if (data !== undefined) {
    config.data = data;
    if (!config.headers['Content-Type'] && method !== 'GET') {
      config.headers['Content-Type'] = 'application/json';
    }
  }

  const resp = await axios(config);
  return {
    data: resp.data,
    status: resp.status,
    headers: resp.headers,
  };
}

/**
 * Live: POST /api/gateway.php with { a, d }
 * Returns { data, status } like axios for easy drop-in.
 */
export async function apiGateway(action, payload = {}, options = {}) {
  const envelope = buildGatewayEnvelope(action, payload);
  const resp = await axios.post(gatewayUrl(), envelope, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: options.withCredentials !== false,
    validateStatus: () => true,
  });
  return {
    data: resp.data,
    status: resp.status,
    headers: resp.headers,
  };
}

/** Keepalive / sendBeacon for enquiry (e1) on pagehide */
export function apiGatewayKeepalive(action, payload = {}) {
  try {
    const envelope = buildGatewayEnvelope(action, payload);
    const body = JSON.stringify(envelope);
    const blob = new Blob([body], { type: 'application/json' });
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(gatewayUrl(), blob);
      return;
    }
    fetch(gatewayUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'include',
      mode: 'cors',
    }).catch((err) =>
      console.warn('[apiGateway keepalive]', err?.message || err)
    );
  } catch (err) {
    console.warn('[apiGateway keepalive]', err?.message || err);
  }
}

// ---------- Apply-form helpers (gateway on live, direct on local) ----------

function localOtpUrl(name) {
  return `/api/${name}`;
}

export async function apiGetState() {
  if (shouldUseApiGateway()) return apiGateway(GW.GET_STATE, {});
  return legacyRequest({
    method: 'GET',
    url: `${API_ORIGIN}/Api/TaskApi/getState`,
  });
}

export async function apiGetCity(stateId) {
  if (shouldUseApiGateway()) {
    return apiGateway(GW.GET_CITY, { state_id: stateId });
  }
  return legacyRequest({
    method: 'POST',
    url: `${API_ORIGIN}/Api/TaskApi/getCity`,
    data: { state_id: stateId },
  });
}

export async function apiGetPincodeByCityId(cityId) {
  if (shouldUseApiGateway()) {
    return apiGateway(GW.GET_PINCODE, { city_id: cityId });
  }
  return legacyRequest({
    method: 'POST',
    url: `${API_ORIGIN}/Api/TaskApi/getPincodeByCityId`,
    data: { city_id: cityId },
  });
}

export async function apiSendOtp(mobile) {
  if (shouldUseApiGateway()) {
    return apiGateway(GW.SEND_OTP, { mobile });
  }
  const params = new URLSearchParams();
  params.append('mobile', mobile);
  return legacyRequest({
    method: 'POST',
    url: localOtpUrl('send-otp'),
    data: params,
    formUrlEncoded: true,
    withCredentials: true,
  });
}

export async function apiVerifyOtp(mobile, otp) {
  if (shouldUseApiGateway()) {
    return apiGateway(GW.VERIFY_OTP, { mobile, otp });
  }
  const params = new URLSearchParams();
  params.append('mobile', mobile);
  params.append('otp', otp);
  return legacyRequest({
    method: 'POST',
    url: localOtpUrl('verify-otp'),
    data: params,
    formUrlEncoded: true,
    withCredentials: true,
  });
}

export async function apiPanBlacklist(pan) {
  if (shouldUseApiGateway()) {
    return apiGateway(GW.PAN_BLACKLIST, { pan, pancard: pan });
  }
  return legacyRequest({
    method: 'GET',
    url: `${API_ORIGIN}/Api/TaskApi/checkGetCustomerbyPanBlacklist`,
    params: { pancard: pan },
  });
}

export async function apiPanCustomer(pan) {
  if (shouldUseApiGateway()) {
    return apiGateway(GW.PAN_CUSTOMER, { pan, pancard: pan });
  }
  return legacyRequest({
    method: 'GET',
    url: `${API_ORIGIN}/Api/TaskApi/checkGetCustomerbyPan`,
    params: { pancard: pan },
  });
}

export async function apiVerifyPanGov(pan) {
  if (shouldUseApiGateway()) {
    return apiGateway(GW.VERIFY_PAN, { pan });
  }
  return legacyRequest({
    method: 'POST',
    url: `${API_ORIGIN}/loan_backend/verifyPan.php`,
    data: { pan },
  });
}

export async function apiSendAadhaarOtp(aadhaar_number) {
  if (shouldUseApiGateway()) {
    return apiGateway(GW.SEND_AADHAAR_OTP, { aadhaar_number });
  }
  return legacyRequest({
    method: 'POST',
    url: `${API_ORIGIN}/loan_backend/sendAadhaarOtp.php`,
    data: { aadhaar_number },
  });
}

export async function apiVerifyAadhaarOtp(body) {
  if (shouldUseApiGateway()) {
    return apiGateway(GW.VERIFY_AADHAAR_OTP, body);
  }
  return legacyRequest({
    method: 'POST',
    url: `${API_ORIGIN}/loan_backend/verifyAadhaarOtp.php`,
    data: body,
  });
}

export async function apiSaveEnquiry(payload) {
  if (shouldUseApiGateway()) {
    return apiGateway(GW.SAVE_ENQUIRY, payload);
  }
  return legacyRequest({
    method: 'POST',
    url: `${API_ORIGIN}/loan_backend/saveEnquiry.php`,
    data: payload,
  });
}

export function apiSaveEnquiryKeepalive(payload) {
  if (shouldUseApiGateway()) {
    apiGatewayKeepalive(GW.SAVE_ENQUIRY, payload);
    return;
  }
  try {
    const body = JSON.stringify(payload);
    const blob = new Blob([body], { type: 'application/json' });
    const url = `${API_ORIGIN}/loan_backend/saveEnquiry.php`;
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(url, blob);
      return;
    }
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      mode: 'cors',
    }).catch(() => {});
  } catch {
    /* ignore */
  }
}

export async function apiSaveLead(payload) {
  if (shouldUseApiGateway()) {
    return apiGateway(GW.SAVE_LEAD, payload, { withCredentials: true });
  }
  // Local: existing PHP proxy injects OTP from session
  return legacyRequest({
    method: 'POST',
    url: localOtpUrl('save-application'),
    data: payload,
    withCredentials: true,
  });
}
